import { jsPDF } from "jspdf";
import "jspdf-autotable";

export function generateOrderInvoicePdf(order, skuMaster, company) {
    const doc = new jsPDF({
        unit: "mm",
        format: [80, 200], // 80mm width, height will be adjusted if needed (auto)
    });

    const width = 80;
    let y = 10;

    // Header
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    const headerName = order.distributorName || company.name || "QUICKBILL";
    doc.text(headerName.toUpperCase(), width / 2, y, { align: "center" });
    y += 6;

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    if (company.address1) {
        doc.text(company.address1, width / 2, y, { align: "center" });
        y += 4;
    }
    if (company.phone) {
        doc.text(`Tel: ${company.phone}`, width / 2, y, { align: "center" });
        y += 4;
    }
    if (company.gstin) {
        doc.text(`GSTIN: ${company.gstin}`, width / 2, y, { align: "center" });
        y += 5;
    }

    doc.setLineWidth(0.2);
    doc.line(5, y, width - 5, y);
    y += 5;

    // Order Info
    doc.setFont("helvetica", "bold");
    doc.text("INVOICE", width / 2, y, { align: "center" });
    y += 5;

    doc.setFont("helvetica", "normal");
    const orderDate = order.createdAt?.toDate ? order.createdAt.toDate() : (order.createdAt instanceof Date ? order.createdAt : new Date());
    doc.text(`Date: ${orderDate.toLocaleString()}`, 5, y);
    y += 4;
    doc.text(`Invoice #: ${order.invoiceNo || "N/A"}`, 5, y);
    y += 4;
    doc.text(`Outlet: ${order.outletName}`, 5, y);
    y += 4;
    doc.text(`Beat: ${order.beat}`, 5, y);
    y += 6;

    // Items Header
    doc.setFont("helvetica", "bold");
    doc.text("Item", 5, y);
    doc.text("Qty", 45, y, { align: "right" });
    doc.text("Basic", 60, y, { align: "right" });
    doc.text("Total", width - 5, y, { align: "right" });
    y += 2;
    doc.line(5, y, width - 5, y);
    y += 5;

    // Items
    doc.setFont("helvetica", "normal");
    
    // If itemLines exist, use them directly (Historical Data)
    if (order.itemLines && order.itemLines.length > 0) {
        order.itemLines.forEach((line) => {
            const name = (line.skuName || "").substring(0, 24);
            doc.text(name, 5, y);
            doc.text(`${line.qty}`, 45, y, { align: "right" });
            doc.text(`${Number(line.basicAmount || 0).toFixed(2)}`, 60, y, { align: "right" });
            doc.text(`${Number(line.totalAmount || 0).toFixed(2)}`, width - 5, y, { align: "right" });

            y += 3;
            doc.setFontSize(6);
            doc.text(`(${line.hsn || "N/A"})`, 5, y);
            doc.setFontSize(8);
            y += 6;

            if (y > 180) {
                doc.addPage([80, 200]);
                y = 10;
            }
        });
    } else {
        // Fallback for legacy orders without itemLines
        Object.entries(order.items || {}).forEach(([sku, qty]) => {
            const m = skuMaster[sku] || {};
            const basicEway = Number(m.Basic_Eway) || 0;
            const gstP = Number(m.gst) || 0;
            const mrp = Number(m.mrp) || 0;
            const skuType = (m.skutype || "").toLowerCase();

            let lineBasic = 0;
            let lineTax = 0;

            if (skuType === "tobacco") {
                lineBasic = qty * basicEway;
                const unitBasicRate = mrp / ((100 + gstP) / 100);
                const unitTax = mrp - unitBasicRate;
                lineTax = qty * unitTax;
            } else {
                lineBasic = qty * basicEway;
                lineTax = lineBasic * (gstP / 100);
            }
            const lineTotal = lineBasic + lineTax;

            const name = (m.Name || sku).substring(0, 24);
            doc.text(name, 5, y);
            doc.text(`${qty}`, 45, y, { align: "right" });
            doc.text(`${lineBasic.toFixed(2)}`, 60, y, { align: "right" });
            doc.text(`${lineTotal.toFixed(2)}`, width - 5, y, { align: "right" });

            y += 3;
            doc.setFontSize(6);
            doc.text(`(${m.hsn || "N/A"})`, 5, y);
            doc.setFontSize(8);
            y += 6;

            if (y > 180) {
                doc.addPage([80, 200]);
                y = 10;
            }
        });
    }

    doc.line(5, y, width - 5, y);
    y += 6;

    // Totals - Use stored totals
    doc.setFont("helvetica", "bold");
    doc.text("Basic Total:", 45, y, { align: "right" });
    doc.text(`${(order.totalBasic || 0).toFixed(2)}`, width - 5, y, { align: "right" });
    y += 5;
    doc.text("CGST Amount:", 45, y, { align: "right" });
    doc.text(`${(order.cgst || 0).toFixed(2)}`, width - 5, y, { align: "right" });
    y += 5;
    doc.text("SGST Amount:", 45, y, { align: "right" });
    doc.text(`${(order.sgst || 0).toFixed(2)}`, width - 5, y, { align: "right" });
    y += 7;

    doc.setFontSize(10);
    doc.text("Grand Total:", 45, y, { align: "right" });
    doc.text(`Rs. ${(order.totalAmount || 0).toFixed(2)}`, width - 5, y, { align: "right" });
    y += 10;

    // Tax Breakup Section - Re-generating from itemLines to ensure accuracy without external SKU master dependency
    const breakup = {};
    if (order.itemLines && order.itemLines.length > 0) {
        order.itemLines.forEach(line => {
            const gstP = (Number(line.cgstRate || 0) + Number(line.sgstRate || 0));
            if (!breakup[gstP]) breakup[gstP] = { taxable: 0, cgst: 0, sgst: 0 };
            breakup[gstP].taxable += Number(line.basicAmount || 0);
            breakup[gstP].cgst += Number(line.cgstAmount || 0);
            breakup[gstP].sgst += Number(line.sgstAmount || 0);
        });
    } else {
        Object.entries(order.items || {}).forEach(([sku, qty]) => {
            const m = skuMaster[sku] || {};
            const basicEway = Number(m.Basic_Eway) || 0;
            const gstP = Number(m.gst) || 0;
            const mrp = Number(m.mrp) || 0;
            const skuType = (m.skutype || "").toLowerCase();

            let lineBasic = qty * basicEway;
            let lineTax = 0;
            if (skuType === "tobacco") {
                const unitBasicRate = mrp / ((100 + gstP) / 100);
                lineTax = qty * (mrp - unitBasicRate);
            } else {
                lineTax = lineBasic * (gstP / 100);
            }

            if (!breakup[gstP]) {
                breakup[gstP] = { taxable: 0, cgst: 0, sgst: 0 };
            }
            breakup[gstP].taxable += lineBasic;
            breakup[gstP].cgst += lineTax / 2;
            breakup[gstP].sgst += lineTax / 2;
        });
    }

    if (Object.keys(breakup).length > 0) {
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.text("Tax Breakup:", 5, y);
        y += 4;
        doc.setFontSize(7);
        doc.text("GST%", 5, y);
        doc.text("Taxable", 25, y, { align: "right" });
        doc.text("CGST Amt", 50, y, { align: "right" });
        doc.text("SGST Amt", width - 5, y, { align: "right" });
        y += 2;
        doc.line(5, y, width - 5, y);
        y += 4;
        doc.setFont("helvetica", "normal");
        Object.entries(breakup).sort((a, b) => Number(b[0]) - Number(a[0])).forEach(([gst, data]) => {
            doc.text(`${gst}%`, 5, y);
            doc.text(data.taxable.toFixed(2), 25, y, { align: "right" });
            doc.text(data.cgst.toFixed(2), 50, y, { align: "right" });
            doc.text(data.sgst.toFixed(2), width - 5, y, { align: "right" });
            y += 4;
        });
        y += 5;
    }

    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");
    doc.text("Thank you for your business!", width / 2, y, { align: "center" });
    y += 4;
    doc.setFontSize(6);
    doc.setFont("helvetica", "normal");
    doc.text("Powered by Quickbill", width / 2, y, { align: "center" });

    return doc;
}
